/**
 * Idle-time prefetch for Opening / Closing pool period bundles.
 *
 * Mirrors prefetchLogJobChunk + prefetchLogJobHistory patterns:
 *   - deduped in-flight promises (prefetch + usePoolPeriodVisits share one fetch)
 *   - seeds poolPeriodInstantCache so first paint on the Opening tab is accurate
 *   - scheduled during requestIdleCallback so it never blocks board first paint
 */

import { fetchBulkLifecyclePeriodBundles } from '../../services/lifecycleBundleService';
import { prefetchClosedStretchBundles } from '../../services/closedStretchVisitsService';
import { clearStoredClosedStretch, invalidateStoredClosedStretchForCustomer } from '../schedule/closedStretchVisits';
import {
  clearPoolPeriodInstantCache,
  invalidatePoolPeriodInstantCacheForCustomer,
} from '../schedule/poolPeriodInstantCount';
import { getFieldReadTables } from '../job/fieldReadTables';
import {
  applyJobsListMutationToBundle,
  applyJobsListMutationToBundleMap,
  registerJobsListMutationStoreApplier,
} from '../job/jobsListMutationSignal';
import { supabase } from '../../supabase';

/** @type {Map<string, Promise<Map>>} */
const inflight = new Map();

/**
 * Last authoritative bundle PER CUSTOMER, keyed `tenantId:context:customerId`.
 * This is the single source of truth that survives tab switches and PWA resume:
 * usePoolPeriodVisits lazy-inits from it, so the Opening/Closing card renders the
 * correct, billed-excluded count on the FIRST paint instead of passing through an
 * undefined-bundle window (which is where the count used to fall back to an
 * over-count). Per-customer (not per customer-set) so it's immune to set/date
 * key drift. Cleared only on SIGNED_OUT (same lifetime as the instant cache).
 * @type {Map<string, { jobCount: number, total: number, jobs: Array, windowStart: string|null, billedJobIds: string[] }>}
 */
const resolvedBundles = new Map();

/**
 * Job ids a successful whole-job delete already accepted. An in-flight bundle
 * fetch that started before the RPC can otherwise write the deleted visit
 * back into the store and resurrect the green pill. Session-scoped; cleared
 * on SIGNED_OUT / test reset. UUIDs do not recycle.
 * @type {Set<string>}
 */
const removedJobIds = new Set();
/** Jobs a realtime INSERT already accepted. An in-flight bundle fetch that
 * started before the write must not overwrite the new green pill. */
const pendingUpserts = new Map();

function stripRemovedJobsFromBundle(bundle) {
  if (!bundle || removedJobIds.size === 0) return bundle;
  return applyJobsListMutationToBundle(bundle, {
    type: 'remove',
    jobIds: [...removedJobIds],
  });
}

function stripRemovedJobsFromMap(resultMap) {
  if (!resultMap || typeof resultMap.forEach !== 'function' || removedJobIds.size === 0) {
    return resultMap;
  }
  return applyJobsListMutationToBundleMap(resultMap, {
    type: 'remove',
    jobIds: [...removedJobIds],
  });
}

function mergePendingUpsertsIntoBundle(bundle, context, customerId) {
  if (!bundle || pendingUpserts.size === 0) return bundle;
  let next = bundle;
  pendingUpserts.forEach((job) => {
    next = applyJobsListMutationToBundle(next, { type: 'upsert', job }, { context, customerId });
  });
  return next;
}

function mergePendingUpsertsIntoMap(resultMap, context) {
  if (!resultMap || typeof resultMap.forEach !== 'function' || pendingUpserts.size === 0) {
    return resultMap;
  }
  let changed = false;
  const next = new Map();
  resultMap.forEach((bundle, customerId) => {
    const merged = mergePendingUpsertsIntoBundle(bundle, context, String(customerId));
    if (merged !== bundle) changed = true;
    next.set(customerId, merged);
  });
  return changed ? next : resultMap;
}

function prunePendingSeenInFetch(resultMap) {
  if (!resultMap || typeof resultMap.forEach !== 'function') return;
  resultMap.forEach((bundle) => {
    for (const j of bundle?.jobs || []) pendingUpserts.delete(String(j.id));
  });
}

/**
 * Write-is-truth for Opening/Closing visit pills. The dashboard `jobs` array
 * is patched separately (useAdminDashboardRealtime); this cache is what
 * PoolCustomerCard actually renders.
 */
export function applyJobsListMutationToResolvedBundles(detail) {
  if (!detail) return;
  if (detail.type === 'remove') {
    for (const id of detail.jobIds || []) {
      if (id != null) {
        const sid = String(id);
        removedJobIds.add(sid);
        pendingUpserts.delete(sid);
      }
    }
  }
  if (detail.type === 'upsert' && detail.job?.id) {
    pendingUpserts.set(String(detail.job.id), detail.job);
  }
  for (const [key, bundle] of [...resolvedBundles]) {
    const parts = String(key).split(':');
    const context = parts[1];
    const customerId = parts[2];
    const next = stripRemovedJobsFromBundle(
      applyJobsListMutationToBundle(bundle, detail, { context, customerId }),
    );
    if (next !== bundle) resolvedBundles.set(key, next);
  }
}

registerJobsListMutationStoreApplier(applyJobsListMutationToResolvedBundles);

function prefetchKey(tenantId, context, customerIds, transitionDate, tz, tableId) {
  const sorted = [...customerIds].map(String).sort().join(',');
  // tz is part of the key (not just an input to the fetch): the window_start
  // resolution is tz-aware (money-fences, 2026-09-17), so a tenant tz change
  // must never dedupe against — or reuse — an in-flight/just-finished fetch
  // keyed off a DIFFERENT tz's window.
  //
  // tableId (field-read-routing fix, 2026-09-18) is likewise part of the key,
  // not just an input to the underlying fetch: fetchBulkLifecyclePeriodBundles
  // resolves base `jobs`/`customers` vs the dollar-free `field_*` views from
  // the STORE-backed getFieldReadTables() at FETCH time, and that store
  // (priceVisibilityStore / currentTenantRoleStore) defaults to
  // ['supervisor'] / null until CompanySettingsContext/AuthContext resolve.
  // A fetch issued during that window can resolve the WRONG table and return
  // 0 rows with no error — without tableId in the key, that wrong-routed
  // fetch would dedupe against (or be served to) a caller resolved under the
  // CORRECT routing moments later, poisoning it with an empty/wrong bundle.
  // Mirrors the useLogJobCustomerPrefetch cache-key fix (CLAUDE.md: "a
  // module-level cache of a ROLE-ROUTED read is keyed by the TABLE it read").
  return `${tenantId}:${context}:${transitionDate}:${tz}:${tableId}:${sorted}`;
}

function bundleStoreKey(tenantId, context, customerId) {
  return `${tenantId}:${context}:${String(customerId)}`;
}

/** Persist each customer's authoritative bundle from a resolved fetch result. */
function storeResolvedBundles(tenantId, context, resultMap) {
  if (!tenantId || !context || !resultMap || typeof resultMap.forEach !== 'function') return;
  resultMap.forEach((bundle, customerId) => {
    if (bundle) {
      resolvedBundles.set(
        bundleStoreKey(tenantId, context, customerId),
        mergePendingUpsertsIntoBundle(
          stripRemovedJobsFromBundle(bundle),
          context,
          String(customerId),
        ),
      );
    }
  });
}

/**
 * Last known authoritative bundle for one customer, or null. Lets a freshly
 * mounted PoolCustomerQuickList show the billed-correct count immediately.
 */
export function getResolvedBundle(tenantId, context, customerId) {
  if (!tenantId || !context || customerId == null) return null;
  return resolvedBundles.get(bundleStoreKey(tenantId, context, customerId)) || null;
}

/**
 * Evict one customer's persisted bundle + instant-cache entry (BOTH contexts).
 * MUST run when the customer's lifecycle status changes: that moves window_start,
 * and both caches key the bundle/window by (tenant, context, customer) only —
 * cleared otherwise just on SIGNED_OUT. A stale window lets pre-window jobs through
 * → the route board paints an over-count ("4 visits") that flips to the correct
 * count once the fresh bundle lands. Wiring this at the status-change write
 * (setCustomerLifecycleStatus) keeps the first paint always window-correct.
 * customerId is a UUID, so the suffix match is tenant-safe.
 */
export function invalidatePoolPeriodCachesForCustomer(customerId) {
  if (customerId == null) return;
  const suffix = `:${String(customerId)}`;
  for (const key of resolvedBundles.keys()) {
    if (key.endsWith(suffix)) resolvedBundles.delete(key);
  }
  invalidatePoolPeriodInstantCacheForCustomer(customerId);
  invalidateStoredClosedStretchForCustomer(customerId);
}

/**
 * Fetch lifecycle period bundles for a pool context. Idempotent — safe to call
 * from prefetch, hooks, and tab mounts; shares one network round-trip per key.
 *
 * @param {{
 *   tenantId: string,
 *   customerIds: string[],
 *   context: 'opening' | 'closing',
 *   transitionDate: string,
 *   tz?: string,
 * }} params
 * @returns {Promise<Map>}
 */
export function prefetchPoolPeriodBundles({
  tenantId,
  customerIds,
  context,
  transitionDate,
  tz = 'UTC',
}) {
  if (!tenantId || !context || !transitionDate) return Promise.resolve(new Map());
  const ids = (customerIds || []).map(String).filter(Boolean);
  if (ids.length === 0) return Promise.resolve(new Map());

  // Resolve the SAME table identity fetchBulkLifecyclePeriodBundles will
  // resolve internally (both are synchronous store reads with no await
  // between them, so they can never disagree) — see prefetchKey's docblock.
  const tableId = getFieldReadTables().jobs;
  const key = prefetchKey(tenantId, context, ids, transitionDate, tz, tableId);
  const existing = inflight.get(key);
  if (existing) return existing;

  const promise = fetchBulkLifecyclePeriodBundles({
    tenantId,
    customerIds: ids,
    context,
    transitionDate,
    tz,
  })
    .then((result) => {
      // Persist the authoritative per-customer bundles so a later mount (tab
      // switch / PWA resume) renders the correct count on first paint.
      // Strip ids a delete RPC already accepted, then merge ids a realtime
      // INSERT already accepted — an in-flight fetch must not resurrect a
      // deleted pill or drop a just-logged one.
      prunePendingSeenInFetch(result);
      const stripped = stripRemovedJobsFromMap(result);
      const merged = mergePendingUpsertsIntoMap(stripped, context);
      storeResolvedBundles(tenantId, context, merged);
      return merged;
    })
    .catch((err) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[prefetchPoolPeriod] ${context} failed —`, err?.message ?? err);
      }
      // A non-enumerable marker — never touches Map size/iteration/toMatchObject
      // comparisons — so a caller that wants to distinguish "the fetch
      // genuinely found nothing" from "the fetch failed" can
      // (usePoolPeriodVisits uses it to avoid blanking an already-good
      // render on a failed BACKGROUND refresh; CLAUDE.md data-freshness
      // doctrine — a failed reconcile must never regress a good render).
      const failedMap = new Map();
      Object.defineProperty(failedMap, '__fetchFailed', { value: true, enumerable: false });
      return failedMap;
    })
    .finally(() => {
      if (inflight.get(key) === promise) inflight.delete(key);
    });

  inflight.set(key, promise);
  return promise;
}

/**
 * Prefetch opening + closing bundles when both customer sets are known.
 *
 * @param {{
 *   tenantId: string,
 *   transitionDate: string,
 *   tz?: string,
 *   openingCustomerIds?: string[],
 *   closingCustomerIds?: string[],
 *   closedCustomerIds?: string[],
 * }} params
 */
export function prefetchPoolPeriodBundlesForTabs({
  tenantId,
  transitionDate,
  tz = 'UTC',
  openingCustomerIds = [],
  closingCustomerIds = [],
  closedCustomerIds = [],
}) {
  const tasks = [];
  if (openingCustomerIds.length) {
    tasks.push(prefetchPoolPeriodBundles({
      tenantId,
      customerIds: openingCustomerIds,
      context: 'opening',
      transitionDate,
      tz,
    }));
  }
  if (closingCustomerIds.length) {
    tasks.push(prefetchPoolPeriodBundles({
      tenantId,
      customerIds: closingCustomerIds,
      context: 'closing',
      transitionDate,
      tz,
    }));
  }
  if (closedCustomerIds.length) {
    tasks.push(prefetchClosedStretchBundles({
      tenantId,
      customerIds: closedCustomerIds,
      transitionDate,
      tz,
    }));
  }
  return Promise.all(tasks);
}

/**
 * Start the season prefetch on the next turn — not idle. The route board is
 * already painted; waiting for requestIdleCallback left Opening / Closing /
 * Closed showing Log and then snapping to Logged.
 * Returns a cancel function for effect cleanup.
 *
 * @param {Parameters<typeof prefetchPoolPeriodBundlesForTabs>[0]} params
 */
export function schedulePrefetchPoolPeriodBundles(params) {
  if (typeof window === 'undefined') return () => {};
  let cancelled = false;
  const id = window.setTimeout(() => {
    if (!cancelled) prefetchPoolPeriodBundlesForTabs(params).catch(() => {});
  }, 0);
  return () => {
    cancelled = true;
    window.clearTimeout(id);
  };
}

/** Test-only reset. */
export function __resetPoolPeriodPrefetchForTests() {
  inflight.clear();
  resolvedBundles.clear();
  removedJobIds.clear();
  pendingUpserts.clear();
  clearPoolPeriodInstantCache();
  clearStoredClosedStretch();
}

/**
 * Whether a Supabase auth event should flush the pool-period caches.
 *
 * ONLY a real sign-out flushes. supabase fires `SIGNED_IN` SPURIOUSLY on token
 * refresh / tab-focus / PWA resume (not just real logins). Flushing the billed-
 * exclusion cache on `SIGNED_IN` wiped it on every resume, so navigating to the
 * Opening/Closing tab right after hit the tag-only fallback and briefly OVER-
 * counted billed jobs ("5 visits → none" flicker) until the bundle reconciled.
 * The cache is tenant-keyed and re-seeded on every bundle fetch, so it never
 * needs a `SIGNED_IN` reset — a genuine account switch always emits `SIGNED_OUT`
 * first, which still clears it.
 */
export function shouldFlushPoolPeriodCachesOnAuthEvent(event) {
  return event === 'SIGNED_OUT';
}

if (supabase?.auth?.onAuthStateChange) {
  supabase.auth.onAuthStateChange((event) => {
    if (shouldFlushPoolPeriodCachesOnAuthEvent(event)) {
      inflight.clear();
      resolvedBundles.clear();
      removedJobIds.clear();
      pendingUpserts.clear();
      clearPoolPeriodInstantCache();
      clearStoredClosedStretch();
    }
  });
}
